// SPDX-FileCopyrightText: 2026 Bundesrepublik Deutschland, vertreten durch das BMI/ITZBund
//
// SPDX-License-Identifier: GPL-3.0-or-later

/*
 * This file is part of the package itzbund/gsb-places of the GSB 11 Project by ITZBund
 *
 * Copyright (C) 2025 Bundesrepublik Deutschland, vertreten durch das
 * BMI/ITZBund. Author: Thomas Maroschik
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import {PickingInfo, Widget} from "@deck.gl/core";
import {GsbMapDeckglControl} from "../components/gsb-map-deckgl-control";
import {WidgetProps} from "@deck.gl/core/src/lib/widget";
import {html, render} from "lit";
import {map} from 'lit/directives/map.js'
import {GsbMapPopup} from "../components/gsb-map-popup";

export type FeaturePopupWidgetProps = WidgetProps & {
  title: string | ((info: PickingInfo, widget: FeaturePopupWidget) => string);
  features: string | ((info: PickingInfo, widget: FeaturePopupWidget) => string);
}

export class FeaturePopupWidget extends Widget<FeaturePopupWidgetProps> {

  placement: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "fill";
  className: string;

  static defaultProps: Required<FeaturePopupWidgetProps> = {
    ...Widget.defaultProps,
    title: undefined!,
    features: undefined!
  };

  private gsbMapDeckglControl?: GsbMapDeckglControl;

  private deck?: GsbMapDeckglControl;

  constructor(props: FeaturePopupWidgetProps) {
    super(props);
    this.setProps(this.props);
  }

  public onAdd(context): HTMLDivElement {
    this.gsbMapDeckglControl = context.deck.props.gsbMapDeckglControl || null;
    this.deck = context.deck;
    this.deck.canvas.addEventListener('mousedown', this.preventFocus);
    return null;
  }

  public onRemove() {
    this.deck.canvas.removeEventListener('mousedown', this.preventFocus);
    this.deck = undefined;
    this.gsbMapDeckglControl = undefined;
  }

  public onClick = (pickingInfo: PickingInfo, event) => {
    if (!this.gsbMapDeckglControl || !pickingInfo.object) {
      return;
    }
    // This is necessary as the widget gets the pointerup event and cannot prevent the issuance of the subsequent click
    // event that is registered as outside click by the popup.
    setTimeout(() => {
      const popup = new GsbMapPopup();
      popup.closeButton = popup.closeOnMove = popup.closeOnOutsideClick = true;
      popup.coordinates = pickingInfo.coordinate as [number, number];
      popup.addEventListener('gsb-map-popup-closed', () => popup.remove());
      const popupContent = this.renderPopupContent(pickingInfo);
      render(popupContent, popup)
      this.gsbMapDeckglControl.insertAdjacentElement('afterend', popup);
    });
  }

  protected renderPopupContent(pickingInfo: PickingInfo) {
    const title = typeof this.props?.title === 'function' ? this.props.title(pickingInfo, this) : this.props.title || "";
    const features = typeof this.props?.features === 'function' ? this.props.features(pickingInfo, this) : this.props.features || [];
    return html`
      <strong class="gsb-map-popup-feature-title">${title}</strong>
      <table class="gsb-map-popup-feature-table">
        ${map(
          features,
          (feature) => html`
            <tr>
              <th>${typeof feature.label === 'function' ? feature.label(pickingInfo, this) : feature.label || ""}</th>
              <td>${typeof feature.value === 'function' ? feature.value(pickingInfo, this) : feature.value || ""}</td>
            </tr>`
        )}
      </table>
    `;
  }

  public onRenderHTML() {
  }

  public onRedraw({layers}) {
  }

  private preventFocus = (event:Event) => event.preventDefault();
}
